import { types as api } from '@mesa-labs/mesa-api';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ActionItemsAgeFilterOptions, ActionItemsBoardFilters, ActionItemsDoneWindowOptions, ActionItemsGroupBy, ActionItemsGroupByOptions, ActionItemsLayout,
} from '../../utils/vendorActionItems';

export type ActionItemsState = {
  // Optional so state persisted by an older shape still rehydrates; read through selectActionItemsFilters.
  // Named boardFilters (was filters) so the old persisted 'mine' default is dropped in favor of 'all'.
  boardFilters?: Partial<ActionItemsBoardFilters>;
  // Optional for the same reason; read through selectActionItemsLayout and selectActionItemsGroupBy.
  boardLayout?: ActionItemsLayout;
  boardGroupBy?: ActionItemsGroupBy;
};

export const DefaultActionItemsFilters: ActionItemsBoardFilters = {
  scope: 'all',
  vendorId: undefined,
  includeCanceled: false,
  doneWindow: '7d',
  partnerIds: [],
  age: 'all',
};

const isPartner = (value: unknown): value is api.Partners => typeof value === 'number' && api.Partners[value] !== undefined;

const initialState: ActionItemsState = {
  boardFilters: DefaultActionItemsFilters,
};

// Reads the board filters, filling defaults for anything missing or no longer valid in persisted state.
export const selectActionItemsFilters = (state: ActionItemsState): ActionItemsBoardFilters => {
  const filters = state.boardFilters ?? {};
  return {
    scope: filters.scope || DefaultActionItemsFilters.scope,
    vendorId: filters.vendorId || undefined,
    includeCanceled: filters.includeCanceled ?? DefaultActionItemsFilters.includeCanceled,
    doneWindow: ActionItemsDoneWindowOptions.some((option) => option.value === filters.doneWindow)
      ? filters.doneWindow!
      : DefaultActionItemsFilters.doneWindow,
    partnerIds: Array.isArray(filters.partnerIds) ? filters.partnerIds.filter(isPartner) : DefaultActionItemsFilters.partnerIds,
    age: ActionItemsAgeFilterOptions.find((option) => option.value === filters.age)?.value ?? DefaultActionItemsFilters.age,
  };
};

// Reads the board page's layout, falling back to the board for anything missing or no longer valid.
export const selectActionItemsLayout = (state: ActionItemsState): ActionItemsLayout => (
  state.boardLayout === 'table' ? 'table' : 'board'
);

// Reads the board page's swimlane grouping, falling back to assignee for anything missing or no longer valid.
export const selectActionItemsGroupBy = (state: ActionItemsState): ActionItemsGroupBy => (
  ActionItemsGroupByOptions.find((option) => option.value === state.boardGroupBy)?.value ?? 'assignee'
);

const actionItemsSlice = createSlice({
  name: 'actionItems',
  initialState,
  reducers: {
    updateActionItemsFilters(state: ActionItemsState, action: PayloadAction<Partial<ActionItemsBoardFilters>>) {
      state.boardFilters = selectActionItemsFilters({ boardFilters: { ...selectActionItemsFilters(state), ...action.payload } });
    },
    setActionItemsLayout(state: ActionItemsState, action: PayloadAction<ActionItemsLayout>) {
      state.boardLayout = selectActionItemsLayout({ boardLayout: action.payload });
    },
    setActionItemsGroupBy(state: ActionItemsState, action: PayloadAction<ActionItemsGroupBy>) {
      state.boardGroupBy = selectActionItemsGroupBy({ boardGroupBy: action.payload });
    },
  },
});

export const { setActionItemsGroupBy, setActionItemsLayout, updateActionItemsFilters } = actionItemsSlice.actions;
export default actionItemsSlice.reducer;
